import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { customerAuthService } from '@/services/customer-auth.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { useCustomerAutoRefresh } from '@/hooks/use-customer-auto-refresh';

/** Mirrors PlatformAuthInitializer exactly, against the customer auth store. */
export function CustomerAuthInitializer({ children }: { children: React.ReactNode }) {
  const isInitialized = useCustomerAuthStore((s) => s.isInitialized);

  useCustomerAutoRefresh();

  useEffect(() => {
    const { isAuthenticated, accessToken, setInitialUser, clearAuth, setInitialized } =
      useCustomerAuthStore.getState();

    if (!isAuthenticated || !accessToken) {
      setInitialized();
      return;
    }

    customerAuthService
      .getMe()
      .then((user) => {
        setInitialUser(user);
        setInitialized();
      })
      .catch(() => {
        clearAuth();
        setInitialized();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab session sync — see AuthInitializer for rationale. When the shared
  // `customer-auth` entry loses its access token in another tab (logout), this
  // tab drops its in-memory session and CustomerProtectedRoute redirects to login.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'customer-auth') return;
      const { isAuthenticated, clearAuth } = useCustomerAuthStore.getState();
      if (!isAuthenticated) return;

      let stillAuthed = false;
      try {
        if (e.newValue) {
          const parsed = JSON.parse(e.newValue) as { state?: { accessToken?: string | null } };
          stillAuthed = Boolean(parsed.state?.accessToken);
        }
      } catch {
        stillAuthed = false;
      }

      if (!stillAuthed) clearAuth();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-customer-bg">
        <FontAwesomeIcon
          icon={faSpinner}
          className="h-8 w-8 animate-spin text-customer-primary-text"
        />
      </div>
    );
  }

  return <>{children}</>;
}
